import { FeatureFlag } from '@ps/web-core-enums';
import type { EventId, EventType } from '@ps/web-sports-api-types';
import type { Reducer } from 'react';
import React, { useContext, useReducer } from 'react';

import useFeature from '@swf/core/hooks/useFeature';
import { useScoreboardsQuery } from '@swf/generated/graphql/scoreboards/hooks';

import { EventTypeId } from '../types/eventType';

type ComponentId = string;

export type EventTypesEventIdsMap = Partial<Record<`${EventType}`, EventId[] | undefined>>;

type ComponentEventTypeEvents = Partial<Record<ComponentId, EventTypesEventIdsMap>>;

export interface LiveDataState {
  events: ComponentEventTypeEvents;
}

type LiveDataReducerAction =
  | {
      type: 'SET_EVENTS';
      payload: {
        id: ComponentId;
        events: EventTypesEventIdsMap;
      };
    }
  | {
      type: 'REMOVE_EVENTS';
      payload: {
        id: ComponentId;
      };
    };

interface LiveDataContextValue {
  dispatch: React.Dispatch<LiveDataReducerAction>;
  isLoading?: boolean;
  isError?: boolean;
}

const removeDuplicateEventIds = (eventIds: EventId[]): EventId[] => [...new Set(eventIds)];

const LiveDataReducer: Reducer<LiveDataState, LiveDataReducerAction> = (state, action) => {
  switch (action.type) {
    case 'SET_EVENTS': {
      const eventsState = Object.entries(action.payload.events).reduce<EventTypesEventIdsMap>(
        (updatedState, currentValue) => {
          const [eventType, events] = currentValue as [`${EventType}`, EventId[]];
          const componentState = state.events[action.payload.id] ?? [];
          const isEventTypeInComponentState = eventType in componentState;

          if (isEventTypeInComponentState) {
            updatedState[eventType] = [
              ...(state.events[action.payload.id]?.[eventType] ?? []),
              ...events
            ];
          } else {
            updatedState[eventType] = events;
          }

          return updatedState;
        },
        {}
      );

      const LiveDataState = {
        ...state,
        events: {
          ...state.events,
          [action.payload.id]: eventsState
        }
      } as const satisfies LiveDataState;

      return LiveDataState;
    }
    case 'REMOVE_EVENTS': {
      const LiveDataState = {
        ...state,
        events: {
          ...state.events,
          [action.payload.id]: {}
        }
      } as const satisfies LiveDataState;

      return LiveDataState;
    }
  }
};

const mapEventTypesToEventIds = (
  componentEventTypeEvents: ComponentEventTypeEvents
): EventTypesEventIdsMap =>
  Object.entries(componentEventTypeEvents).reduce((previousValue, [, eventTypesEventsMap]) => {
    if (!eventTypesEventsMap || !Object.keys(eventTypesEventsMap).length) {
      return previousValue;
    }

    const eventTypesEventsMapEntries = Object.entries(eventTypesEventsMap);
    const [eventTypeEventsMap] = eventTypesEventsMapEntries;
    const [eventType, events] = eventTypeEventsMap as [`${EventType}`, EventId[]];
    const existingValues = previousValue[eventType] ?? [];

    return {
      ...previousValue,
      [eventType]: removeDuplicateEventIds([...existingValues, ...events])
    };
  }, {} as EventTypesEventIdsMap);

const LiveDataContext = React.createContext<LiveDataContextValue | null>(null);

interface Props extends React.PropsWithChildren {
  initialState?: LiveDataState;
}

export const LiveDataProvider: React.FC<Props> = ({ initialState = { events: {} }, children }) => {
  const isScoreboardsEnabled = useFeature(FeatureFlag.ISPScoreboards);
  const [LiveDataState, dispatch] = useReducer(LiveDataReducer, initialState);

  const eventTypesEventIdsMap = mapEventTypesToEventIds(LiveDataState.events);
  const footballEventIds = eventTypesEventIdsMap[EventTypeId.SOCCER] ?? [];
  const tennisEventIds = eventTypesEventIdsMap[EventTypeId.TENNIS] ?? [];
  const skipPollAttempt = Boolean(
    !isScoreboardsEnabled || (!footballEventIds.length && !tennisEventIds.length)
  );

  const { loading, error } = useScoreboardsQuery({
    variables: {
      footballEventIds: footballEventIds.map(String),
      tennisEventIds: tennisEventIds.map(String)
    },
    // "skip" is used for the initial render
    skip: skipPollAttempt,
    // "skipPollAttempt" is used for every request after the initial render
    skipPollAttempt: () => skipPollAttempt,
    pollInterval: 5000,
    errorPolicy: 'all',
    fetchPolicy: 'network-only'
  });

  return (
    <LiveDataContext.Provider value={{ dispatch, isLoading: loading, isError: Boolean(error) }}>
      {children}
    </LiveDataContext.Provider>
  );
};

export const useLiveData = (): LiveDataContextValue => {
  const context = useContext(LiveDataContext);

  if (context === null) {
    throw new Error('You must wrap "useLiveData" in a "LiveDataProvider"');
  }

  return context;
};
